import React, { useEffect, useState } from "react";
import { Activity, Users, FlaskConical, AlertTriangle, Bandage, CalendarClock, Bug } from "lucide-react";
import { api } from "../lib/api";
import KpiDrilldownModal from "./KpiDrilldownModal";

function Stat({ icon: Icon, label, value, suffix, accent, testid, onClick }) {
  const clickable = typeof onClick === "function";
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      className={`card p-5 md:p-6 fade-up text-left w-full ${clickable ? "cursor-pointer transition-transform hover:scale-[1.02] hover:shadow-[0_0_24px_-8px_var(--accent)] focus:outline-none focus:ring-2 focus:ring-cyan-400/40" : ""}`}
      style={clickable ? { "--accent": accent } : undefined}
      data-testid={testid}
      onClick={onClick}
      type={clickable ? "button" : undefined}
      title={clickable ? "Click for athlete-level details + print" : undefined}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
          <div className="mt-3 flex items-baseline gap-2">
            <div className="font-display font-extrabold text-4xl md:text-5xl" style={{ color: accent }}>
              {value}
            </div>
            {suffix && <div className="text-slate-400 text-sm font-mono-data">{suffix}</div>}
          </div>
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}22`, border: `1px solid ${accent}66` }}
        >
          <Icon size={20} style={{ color: accent }} />
        </div>
      </div>
      {clickable && (
        <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-500 opacity-70">
          Click to view details →
        </div>
      )}
    </Wrapper>
  );
}

export default function HeroKPIs({ overview, injurySummary, onScheduleDrill }) {
  const [sched, setSched] = useState(null);
  const [drilldown, setDrilldown] = useState(null);  // "out_of_range" | "deficiency" | "on_rest" | null
  useEffect(() => {
    let cancelled = false;
    api.get("/squad/schedule/stats")
      .then((r) => { if (!cancelled) setSched(r.data); })
      .catch(() => { /* hide schedule tiles silently if the call fails */ });
    return () => { cancelled = true; };
  }, [overview?.total_records]);  // re-fetch when lab records change so tiles stay live

  if (!overview) return null;
  const topDef = overview.top_deficiencies?.[0];
  const onRest = injurySummary?.on_rest_count ?? 0;
  const activeFlags = injurySummary?.active ?? 0;

  // Combined urgency for the schedule tiles — overdue grouped with due-soon
  // so coaches see a single "action" number per category.
  const labAction = sched ? (sched.counts.lab_overdue + sched.counts.lab_due_soon) : 0;
  const dwAction  = sched ? (sched.counts.deworm_overdue + sched.counts.deworm_due_soon) : 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
        <Stat
          icon={Users}
          label="Athletes"
          value={overview.total_sailors}
          suffix={overview.athletes_added_this_month
            ? `+${overview.athletes_added_this_month} this month`
            : undefined}
          accent="#00E5FF"
          testid="kpi-sailors"
        />
        <Stat
          icon={FlaskConical}
          label="Lab Records"
          value={overview.total_records}
          suffix={overview.labs_added_this_month
            ? `+${overview.labs_added_this_month} this month`
            : "tests"}
          accent="#F59E0B"
          testid="kpi-records"
        />
        <Stat
          icon={AlertTriangle}
          label="Out of Range"
          value={`${overview.pct_out_of_range_latest}%`}
          suffix="latest results"
          accent="#EF4444"
          testid="kpi-out-of-range"
          onClick={() => setDrilldown("out_of_range")}
        />
        <Stat
          icon={Activity}
          label="Top deficiency"
          value={topDef?.label || "—"}
          suffix={topDef ? `${topDef.pct}% affected` : ""}
          accent="#8B5CF6"
          testid="kpi-top-deficiency"
          onClick={topDef ? () => setDrilldown("deficiency") : undefined}
        />
        <Stat
          icon={Bandage}
          label="On rest"
          value={onRest}
          suffix={onRest === 0 ? "all clear" : activeFlags > onRest ? `${activeFlags} open injuries` : "athletes"}
          accent={onRest === 0 ? "#10B981" : "#FB7185"}
          testid="kpi-on-rest"
          onClick={onRest > 0 ? () => setDrilldown("on_rest") : undefined}
        />
      </div>

      {/* Schedule reminders strip — only renders once /squad/schedule/stats returns */}
      {sched && (
        <div className="grid grid-cols-2 gap-3" data-testid="hero-schedule-strip">
          <ScheduleMiniTile
            icon={CalendarClock}
            label="Diagnostics due"
            overdue={sched.counts.lab_overdue}
            due_soon={sched.counts.lab_due_soon}
            total={labAction}
            accent={labAction > 0 ? (sched.counts.lab_overdue > 0 ? "#FB7185" : "#FBBF24") : "#10B981"}
            testid="kpi-diagnostics-due"
          />
          <ScheduleMiniTile
            icon={Bug}
            label="Deworming due"
            overdue={sched.counts.deworm_overdue}
            due_soon={sched.counts.deworm_due_soon}
            total={dwAction}
            accent={dwAction > 0 ? (sched.counts.deworm_overdue > 0 ? "#FB7185" : "#FBBF24") : "#10B981"}
            testid="kpi-deworming-due"
          />
        </div>
      )}

      <KpiDrilldownModal
        open={!!drilldown}
        kind={drilldown}
        onClose={() => setDrilldown(null)}
      />
    </div>
  );
}

function ScheduleMiniTile({ icon: Icon, label, overdue, due_soon, total, accent, testid }) {
  return (
    <div
      className="card p-3 md:p-4 flex items-center gap-3"
      style={{ borderColor: `${accent}66` }}
      data-testid={testid}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}
      >
        <Icon size={18} style={{ color: accent }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
        <div className="flex items-baseline gap-2 mt-0.5">
          <div className="font-display text-2xl md:text-3xl leading-none" style={{ color: accent }}>{total}</div>
          <div className="text-[10px] font-mono-data text-slate-400">
            {overdue > 0 && <span className="text-rose-300">{overdue} overdue</span>}
            {overdue > 0 && due_soon > 0 && <span className="text-slate-500"> · </span>}
            {due_soon > 0 && <span className="text-amber-300">{due_soon} ≤5d</span>}
            {total === 0 && <span className="text-emerald-300">all on track</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
